/**
 * Scale Theme V2 — Animation Controller
 *
 * Global scroll-triggered animation system with progressive enhancement.
 * Elements are visible without JS; hidden only when .animations-ready is set.
 *
 * 2-level cascade:
 * 1. Global preset (minimal/modern/dynamic/elegant/playful)
 * 2. Block (data-animate="inherit|none|fade|...")
 *
 * Sections/groups set data-container-animate to define a default
 * for children. Blocks with "inherit" resolve to the nearest
 * container, falling back to the global preset.
 */

const PRESET_MAP = {
  minimal: 'fade',
  modern: 'fade-up',
  dynamic: 'fade-up',
  elegant: 'fade',
  playful: 'zoom-in',
};

class TextSplitter {
  static #MAX_CHARS = 100;

  static splitWords(el) {
    const html = el.innerHTML.trim();
    if (!html) return;

    el.setAttribute('aria-label', el.textContent.trim());
    let i = 0;
    el.innerHTML = html.replace(/(<[^>]+>)|(&[^;]+;)|([^<&\s]+)/g, (m, tag, entity, word) => {
      if (tag) return tag;
      return `<span class="word"><span class="word-inner" style="--word-index:${i++}">${entity || word}</span></span>`;
    });
    el.dataset.textSplit = 'words';
  }

  static splitChars(el) {
    const text = el.textContent.trim();
    if (!text) return;
    if (text.length > this.#MAX_CHARS) return this.splitWords(el);

    el.setAttribute('aria-label', text);
    let i = 0;
    el.innerHTML = el.innerHTML.trim().replace(/(<[^>]+>)|(&[^;]+;)|([^<&])/g, (m, tag, entity, char) => {
      if (tag) return tag;
      if (char === ' ') return ' ';
      return `<span class="char" style="--char-index:${i++}">${entity || char}</span>`;
    });
    el.dataset.textSplit = 'chars';
  }

  static processAll() {
    document.querySelectorAll('[data-animate="text-words"]:not([data-text-split])').forEach(el => this.splitWords(el));
    document.querySelectorAll('[data-animate="text-chars"]:not([data-text-split])').forEach(el => this.splitChars(el));
  }
}

class AnimationController {
  #controller = new AbortController();
  #observer;
  #mutation;
  #visibilityObserver;
  #animated = new Set();
  #pendingDelays = new Map();
  #preset;

  constructor() {
    this.#preset = window.Scale?.settings?.animations_preset || 'modern';

    // OVA is an editing surface, not a storefront visit. Running entrance
    // animations inside its scaled iframe can leave Chromium compositor layers
    // transparent even after CSS reports opacity: 1. The preview marker is
    // injected in <head>, before this deferred module executes.
    if (document.documentElement.dataset.ovaPreview === 'true') {
      this.#disable();
      return;
    }

    if (this.#prefersReduced()) {
      this.#disable();
      this.#watchReducedMotion();
      return;
    }

    this.#watchReducedMotion();
    this.#init();
  }

  get signal() {
    return this.#controller.signal;
  }

  #init() {
    const setup = () => {
      document.body.classList.add('animations-ready');
      TextSplitter.processAll();
      this.#calculateStagger();
      this.#createObserver();
      this.#observeAll();
      this.#watchMutations();
      this.#watchDesignMode();
      this.#watchResize();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(setup, { timeout: 150 });
    } else {
      setTimeout(setup, 0);
    }
  }

  #createObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.#animated.has(entry.target)) {
          this.#animate(entry.target);
        }
      }
    }, {
      rootMargin: '0px 0px 10% 0px',
      threshold: 0,
    });
  }

  #observeAll() {
    if (!this.#observer) return;

    const els = document.querySelectorAll('[data-animate]:not(.animated):not([data-animate="none"]):not([data-animate-manual])');
    for (const el of els) {
      if (el.offsetParent === null) continue;
      if (el.closest('[data-clone], .slide-clone')) continue;
      this.#observer.observe(el);
    }
  }

  #animate(el) {
    if (this.#animated.has(el)) return;
    this.#animated.add(el);
    this.#observer?.unobserve(el);

    let type = el.getAttribute('data-animate');

    if (type === 'inherit') {
      type = this.#resolveInherit(el);
      if (type && type !== 'none') {
        el.setAttribute('data-animate', type);
      } else {
        el.classList.add('animated');
        return;
      }
    }

    const delayStr = getComputedStyle(el).getPropertyValue('--animate-delay').trim();
    const delay = parseInt(delayStr, 10) || 0;

    if (delay > 0) {
      const timer = setTimeout(() => {
        this.#pendingDelays.delete(el);
        this.#trigger(el);
      }, delay);
      this.#pendingDelays.set(el, timer);
    } else {
      this.#trigger(el);
    }
  }

  #resolveInherit(el) {
    const container = el.closest('[data-container-animate]');
    if (container) {
      const containerType = container.dataset.containerAnimate;
      if (containerType && containerType !== 'none') return containerType;
      if (containerType === 'none') return 'none';
    }
    return PRESET_MAP[this.#preset] || 'fade-up';
  }

  #trigger(el) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('animated');

        el.addEventListener('animationend', () => {
          if (!el.hasAttribute('data-parallax')) {
            el.style.willChange = 'auto';
          }
        }, { once: true });

        // Fallback: clean up will-change if animationend never fires
        setTimeout(() => {
          el.style.willChange = 'auto';
        }, 2000);
      });
    });
  }

  #cancelPending(el) {
    if (this.#pendingDelays.has(el)) {
      clearTimeout(this.#pendingDelays.get(el));
      this.#pendingDelays.delete(el);
    }
  }

  #calculateStagger() {
    const containers = document.querySelectorAll('[data-stagger-children]');
    const ROW_TOLERANCE = 8;

    for (const container of containers) {
      const step = parseInt(container.dataset.staggerChildren, 10) || 100;
      // Shopify wraps blocks in #shopify-block-xxx divs, so animated elements are one level deeper
      const children = container.querySelectorAll(':scope > * [data-animate]:not(.animated), :scope > [data-animate]:not(.animated)');

      if (children.length < 2) continue;

      const sorted = [...children]
        .map(el => ({ el, top: el.getBoundingClientRect().top, left: el.getBoundingClientRect().left }))
        .sort((a, b) => (a.top - b.top) || (a.left - b.left));

      let rowIndex = -1;
      let lastTop = -Infinity;

      for (const { el, top } of sorted) {
        if (top - lastTop > ROW_TOLERANCE) {
          rowIndex = 0;
          lastTop = top;
        } else {
          rowIndex++;
        }
        el.style.setProperty('--animate-delay', `${rowIndex * step}ms`);
      }
    }
  }

  #watchMutations() {
    let pending = false;

    this.#mutation = new MutationObserver((mutations) => {
      if (pending) return;

      let hasNew = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.hasAttribute?.('data-animate') || node.querySelector?.('[data-animate]'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (!hasNew) return;

      pending = true;
      requestAnimationFrame(() => {
        TextSplitter.processAll();
        requestAnimationFrame(() => {
          pending = false;
          this.#calculateStagger();
          this.#observeAll();
        });
      });
    });

    this.#mutation.observe(document.body, { childList: true, subtree: true });

    // Watch for aria-hidden changes (tabs, accordions, etc.)
    // When a container becomes visible, re-observe its animated children
    this.#visibilityObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'aria-hidden') continue;
        const el = m.target;
        if (el.getAttribute('aria-hidden') !== 'false') continue;

        requestAnimationFrame(() => {
          const children = el.querySelectorAll('[data-animate]:not(.animated)');
          for (const child of children) {
            if (child.offsetParent !== null) {
              this.#observer?.observe(child);
            }
          }
        });
      }
    });

    this.#visibilityObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true,
    });
  }

  #watchDesignMode() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        TextSplitter.processAll();
        this.#calculateStagger();
        this.#observeAll();
      }, 100);
    }, { signal: this.signal });

    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target;
      if (!section) return;
      section.querySelectorAll('[data-animate]').forEach(el => {
        this.#cancelPending(el);
        this.#observer?.unobserve(el);
        this.#animated.delete(el);
      });
    }, { signal: this.signal });
  }

  #watchResize() {
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.#calculateStagger(), 250);
    }, { signal: this.signal, passive: true });
  }

  #prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #watchReducedMotion() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', (e) => {
      if (e.matches) {
        this.#disable();
        this.#destroy();
      } else {
        this.#enable();
        this.#init();
      }
    }, { signal: this.signal });
  }

  #disable() {
    document.documentElement.classList.add('animations-disabled');
    document.body.classList.remove('animations-ready');
  }

  #enable() {
    document.documentElement.classList.remove('animations-disabled');
  }

  refresh() {
    TextSplitter.processAll();
    this.#calculateStagger();
    setTimeout(() => this.#observeAll(), 50);
  }

  animate(target) {
    const els = typeof target === 'string'
      ? document.querySelectorAll(target)
      : target instanceof Element ? [target] : target;

    for (const el of els) {
      if (el?.hasAttribute('data-animate')) {
        this.#animate(el);
      }
    }
  }

  #destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mutation?.disconnect();
    this.#mutation = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#pendingDelays.forEach(t => clearTimeout(t));
    this.#pendingDelays.clear();
    this.#animated.clear();
    document.body.classList.remove('animations-ready');
  }
}

const instance = new AnimationController();

window.Scale = window.Scale || {};
window.Scale.animations = {
  refresh: () => instance.refresh(),
  animate: (target) => instance.animate(target),
};
